import {
  formatPhoneDisplay,
  PhoneValidationSchema,
} from '@airship-platform/common/validation';
import { FlexBox, ItemSelector } from '@airship-platform/jam-web/components';
import { PlusIcon } from '@airship-platform/jam-web/icons';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { type FollowUpPhone } from './followUpMessage.utils';

const MAX_PHONES_VISIBLE = 12;

export const PhonesInputSchema = z.object({
  phoneFilter: z.string(),
  smsPhones: PhoneValidationSchema.array(),
});

export type PhonesInputValues = z.infer<typeof PhonesInputSchema>;

interface OwnProps {
  phones: FollowUpPhone[];
  isLoading: boolean;
}

const labelOf = (phone: FollowUpPhone) =>
  phone.memo ? `${phone.memo} · ${phone.display}` : phone.display;

const helperTextOf = (
  error: string | undefined,
  filter: string,
  selectedCount: number
) => {
  if (error) {
    return error;
  }

  if (filter) {
    return 'Press enter or click the plus icon to add this number';
  }

  return selectedCount
    ? `${selectedCount} number${selectedCount === 1 ? '' : 's'} selected`
    : 'Choose a mobile number, or type another one';
};

const FollowUpPhonesInput: React.FC<OwnProps> = ({ phones, isLoading }) => {
  const [customPhones, setCustomPhones] = useState<FollowUpPhone[]>([]);
  const { control, watch, setValue, setError, clearErrors } =
    useFormContext<PhonesInputValues>();

  const [phoneFilter, smsPhones] = watch(['phoneFilter', 'smsPhones']);
  const phoneList = [...customPhones, ...phones];

  const handleAddPhone = (input: string) => {
    const parsed = PhoneValidationSchema.safeParse(input);

    if (!parsed.success) {
      setError('phoneFilter', {
        type: 'manual',
        message: 'Please enter a valid mobile number',
      });
      return;
    }

    if (!parsed.data) {
      return;
    }

    if (phoneList.some((phone) => phone.value === parsed.data)) {
      setError('phoneFilter', {
        type: 'manual',
        message: 'This number is already added',
      });
      return;
    }

    setCustomPhones((current) => [
      ...current,
      {
        value: parsed.data,
        display: formatPhoneDisplay(parsed.data),
        memo: null,
      },
    ]);
    clearErrors('phoneFilter');
    setValue('phoneFilter', '');
    setValue('smsPhones', [...smsPhones, parsed.data]);
  };

  return (
    <FlexBox flexDirection={'column'} gap={2} width={'100%'}>
      <Controller
        name={'phoneFilter'}
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            value={formatPhoneDisplay(field.value)}
            type={'tel'}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position={'end'}>
                    <IconButton onClick={() => handleAddPhone(field.value)}>
                      <PlusIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                onKeyDown: (event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddPhone(field.value);
                  }
                },
              },
            }}
            error={!!fieldState.error}
            helperText={helperTextOf(
              fieldState.error?.message,
              field.value,
              smsPhones.length
            )}
            placeholder={'Search or add a mobile number'}
          />
        )}
      />

      <Controller
        name={'smsPhones'}
        control={control}
        render={({ field }) => (
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            }}
          >
            {phoneList
              .filter(
                (phone) =>
                  phone.display.includes(phoneFilter) ||
                  phone.value.includes(phoneFilter) ||
                  field.value.includes(phone.value)
              )
              .slice(0, MAX_PHONES_VISIBLE)
              .map((phone) => (
                <ItemSelector
                  key={phone.value}
                  label={labelOf(phone)}
                  value={phone.value}
                  checked={field.value.includes(phone.value)}
                  disabled={isLoading}
                  type={'checkbox'}
                  fullWidth
                  handleChange={(event) => {
                    clearErrors('phoneFilter');
                    field.onChange(
                      event.target.checked
                        ? [...field.value, phone.value]
                        : field.value.filter((value) => value !== phone.value)
                    );
                  }}
                />
              ))}
          </Box>
        )}
      />

      {smsPhones.length > 1 && (
        <Alert severity={'warning'}>
          Selecting more than one number sends a single group text.
        </Alert>
      )}
    </FlexBox>
  );
};

export default FollowUpPhonesInput;
